// src/contexts/ScreenTypeContext.tsx
"use client"; // This directive makes it a Client Component

import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { BREAKPOINTS, ScreenType } from "@/app/lib/ScreenTypes"; // Adjust path as needed

interface ScreenTypeContextProps {
  screenType: ScreenType
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  isBigscreen: boolean
}

const ScreenTypeContext = createContext<ScreenTypeContextProps | undefined>(undefined);

const getScreenType = (width: number): ScreenType => {
  if (width <= BREAKPOINTS.mobile) {
    return 'mobile';
  }
  if (width <= BREAKPOINTS.tablet) {
    return 'tablet';
  }

  if (width <= BREAKPOINTS.destktop) {
    return 'desktop'
  }
  return "bigscreen"
};

export const ScreenTypeProvider = ({ children }: { children: ReactNode }) => {
  const [screenType, setScreenType] = useState<ScreenType>('loading'); // Initial state

  useEffect(() => {
    // Ensure this code runs only on the client
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      setScreenType(getScreenType(window.innerWidth));
    };

    // Set initial screen type
    handleResize();

    window.addEventListener('resize', handleResize);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Empty dependency array ensures this runs once on mount and cleans up on unmount

  const contextValue: ScreenTypeContextProps = {
    screenType,
    isMobile: screenType === "mobile",
    isTablet: screenType === "tablet",
    isDesktop: screenType === "desktop",
    isBigscreen: screenType === "bigscreen"
  }

  return (
    <ScreenTypeContext.Provider value={contextValue}>
      {children}
    </ScreenTypeContext.Provider>
  );
};

export const useScreenType = (): ScreenTypeContextProps => {
  const context = useContext(ScreenTypeContext);
  if (context === undefined) {
    throw new Error('useScreenType must be used within a ScreenTypeProvider');
  }
  return context;
};